/**
 * 续住方式选择
 */
import React, { useImperativeHandle, useState, useRef, useEffect } from 'react';
import { Drawer, Spin, Form, Button, message } from 'antd';
import intl from 'react-intl-universal';

import { filterFormParams } from '@/utils/transform';
import { getAllChannelWithCache, getRoomTypeListWithCache } from '@/api/cacheRequest';
import checkInService from '@/api/checkIn';
import { DrawerHeader, DrawerFooter } from '@/components/PortalDrawer';

import useDirectRenewalPortal from '../directRenewal';
import useNormalRenewalPortal from '../normalRenewal';
import RoomInfo, { OrderRoomInfo } from './components/RoomInfo';
import MethodInfo, {
  CreateNewOptionSymbol,
  OptionAllSymbol,
  OrderOptionItem,
} from './components/MethodInfo';
import RemarkInfo from './components/RemarkInfo';

import styles from './index.module.scss';

interface OriginOrderInfo {
  resvNo: string; // 主单id
  recList: string[]; // 子单列表
  onEnd: (isModify: boolean) => void; // 操作回调
}

export interface RenewalModalRef {
  showModal: (data: OriginOrderInfo) => void;
}

const Index = React.forwardRef<RenewalModalRef>((_props, ref) => {
  const [modalVisible, setVisible] = useState(false);
  const [isLoading, setLoadingStatus] = useState(false);
  const [roomInfo, setRoomInfo] = useState<OrderRoomInfo>();
  const [orderList, setOrderList] = useState<OrderOptionItem[]>([]);
  const [methodValue, setMethodValue] = useState<string>(null);
  const [formOption, setFormOption] = useState<{
    roomTypeList: { label: string; value: number }[];
    channelList: { label: string; value: number }[];
  }>({
    roomTypeList: [],
    channelList: [],
  });
  const [form] = Form.useForm();
  const originInfoRef = useRef<OriginOrderInfo>();

  // 续住直连预订
  const directRenewalPortal = useDirectRenewalPortal();
  // 新续住
  const normalRenewalPortal = useNormalRenewalPortal();

  // 关闭选择
  const closeModal = () => {
    setVisible(false);
  };

  // 筛选选项
  const initFormOption = async () => {
    try {
      const [roomTypeList, channelList] = await Promise.all([
        getRoomTypeListWithCache()
          .then((res) => res.data || [])
          .catch(() => []),
        getAllChannelWithCache()
          .then((res) => res.data || [])
          .catch(() => []),
      ]);
      setFormOption({
        roomTypeList: [{ label: intl.get('app_select_all'), value: OptionAllSymbol }].concat(
          roomTypeList.map((item) => ({
            label: item.name,
            value: item.id,
          })),
        ),
        channelList: [{ label: intl.get('app_select_all'), value: OptionAllSymbol }].concat(
          channelList.map((item) => ({
            label: item.name,
            value: item.id,
          })),
        ),
      });
    } catch (_) {
      //
    }
  };

  // 获取可续住订单列表
  const getOrderList = async () => {
    setLoadingStatus(true);
    let list: OrderOptionItem[] = [];
    try {
      const { recList } = originInfoRef.current;
      const { keyWord, channelValue } = form.getFieldsValue();
      const res = await checkInService.getExtensionOrderList({
        filterCondition: filterFormParams({
          keyWord,
          channelIds: channelValue && channelValue !== OptionAllSymbol ? [channelValue] : undefined,
        }),
        recNos: recList,
      });
      list = res.data || [];
    } catch (_) {
      //
    }
    // 订单被过滤掉 置空
    if (
      methodValue !== CreateNewOptionSymbol &&
      !list.some((item) => item.resvNo === methodValue)
    ) {
      setMethodValue(null);
    }
    setOrderList(list);
    setLoadingStatus(false);
  };

  // 显示弹框
  const showModal = (data: OriginOrderInfo) => {
    const { resvNo, recList } = data || {};
    if (resvNo && recList && recList.length) {
      originInfoRef.current = data;
      form.resetFields();
      checkInService
        .batchGetOrderInfo({
          recNos: data.recList,
          resvNo: data.resvNo,
        })
        .then((res) => setRoomInfo(res.data));
      setMethodValue(null);
      setOrderList([]);
      getOrderList();
      setVisible(true);
    } else {
      message.error(intl.get('hooks_order_no_empty'));
    }
  };

  // 提交
  const handleSubmitClick = () => {
    const orderItem = orderList.find((item) => item.resvNo === methodValue);
    const isCreateNew = methodValue === CreateNewOptionSymbol;
    if (orderItem || isCreateNew) {
      const { resvNo, recList, onEnd } = originInfoRef.current;
      if (isCreateNew) {
        normalRenewalPortal.showDrawer({
          mainOrderId: resvNo,
          recList,
          onEnd,
        });
      }
      if (orderItem) {
        directRenewalPortal.showDrawer({
          mainOrderId: resvNo,
          reserveOrderNo: orderItem.resvNo,
          recList,
          onEnd,
        });
      }
      closeModal();
    } else {
      message.error(intl.get('drawer_extension_picker_method_null'));
    }
  };

  useImperativeHandle(ref, () => ({ showModal }), []);

  useEffect(() => {
    initFormOption();
  }, []);

  return (
    <Drawer
      closable={false}
      destroyOnClose
      className={styles.renewalEntryDrawer}
      open={modalVisible}
    >
      <Spin spinning={isLoading}>
        <DrawerHeader title={intl.get('drawer_extension_picker_title')} onClose={closeModal} />
        <div className={styles.contentWrapper}>
          <RoomInfo data={roomInfo} />
          <MethodInfo
            form={form}
            formOption={formOption}
            dataSource={orderList}
            onSearch={getOrderList}
            value={methodValue}
            onChange={setMethodValue}
          />
          <RemarkInfo />
        </div>
        <DrawerFooter>
          <Button type="primary" onClick={handleSubmitClick}>
            {intl.get('app_next')}
          </Button>
          <Button type="default" onClick={closeModal}>
            {intl.get('app_btn_cancel')}
          </Button>
        </DrawerFooter>
      </Spin>
    </Drawer>
  );
});

export default Index;
